import React, { useCallback, useEffect, useState } from "react";
import { Link } from "react-router-dom";
import { toast } from "sonner";
import { QRCodeSVG } from "qrcode.react";
import { ArrowLeft, Copy, Download, Share2, Trash2 } from "lucide-react";
import api from "@/lib/api";
import useCrmConfig from "@/hooks/useCrmConfig";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Loading } from "@/components/common/Loading";

const FIELD_LABELS = {
  display_name: "Name", headline: "Headline", handle: "Startup Otter handle",
  email: "Email", phone: "Phone", org: "Organization", title: "Title", city: "City",
  website: "Website", linkedin: "LinkedIn",
};

export default function ContactCard() {
  const { visible, loading } = useCrmConfig();
  const [card, setCard] = useState(null);
  const [shares, setShares] = useState([]);
  const [newShare, setNewShare] = useState(null);
  const [saving, setSaving] = useState(false);

  const load = useCallback(async () => {
    const [c, s] = await Promise.all([api.get("/crm/card"), api.get("/crm/card/shares")]);
    setCard(c.data);
    setShares(s.data.shares || []);
  }, []);

  useEffect(() => { if (visible) load().catch(() => setCard(null)); }, [visible, load]);

  const toggle = (f) => setCard((c) => ({ ...c, fields: { ...c.fields, [f]: !c.fields[f] } }));
  const setValue = (f, value) => setCard((c) => ({ ...c, values: { ...c.values, [f]: value } }));

  const save = async () => {
    setSaving(true);
    try {
      const { data } = await api.put("/crm/card", { fields: card.fields, values: card.values });
      setCard(data);
      toast.success("Contact card saved");
    } catch { toast.error("Couldn't save your card"); } finally { setSaving(false); }
  };

  const createShare = async () => {
    try {
      const { data } = await api.post("/crm/card/shares", { expires_in_days: 30 });
      setNewShare(data);
      load();
    } catch (e) {
      toast.error(e?.response?.data?.detail === "crm_card_no_fields_selected"
        ? "Select at least one field to share" : "Couldn't create a share link");
    }
  };

  const revoke = async (id) => {
    try { await api.delete(`/crm/card/shares/${id}`); load(); }
    catch { toast.error("Couldn't revoke that link"); }
  };

  if (loading) return <Loading />;
  if (!visible) return (
    <div className="py-20 text-center" data-testid="card-unavailable">
      <h1 className="text-2xl font-[680]">Page not found</h1>
    </div>
  );
  if (!card) return <Loading />;

  const shareUrl = newShare
    ? `${window.location.origin}/dashboard/contacts/card/${newShare.token}` : "";
  const selectedCount = Object.values(card.fields).filter(Boolean).length;

  return (
    <div data-testid="contact-card-page">
      <Link to="/dashboard/contacts" data-testid="card-back"
        className="inline-flex items-center gap-1.5 text-sm font-[560] text-muted-foreground hover:text-foreground">
        <ArrowLeft className="h-4 w-4" /> Back to contacts
      </Link>
      <h1 className="mt-4 text-2xl font-[680] tracking-[-0.01em]">My contact card</h1>
      <p className="mt-1 text-muted-foreground">
        Only the fields you tick are ever shared. Your notes, tags, relationship history and
        follow-up settings can never appear here.
      </p>

      <div className="mt-6 grid gap-8 lg:grid-cols-[minmax(0,1fr)_340px]">
        <section className="rounded-xl border border-border bg-card p-4" data-testid="card-fields">
          <h2 className="text-base font-[640]">What you share</h2>
          <ul className="mt-3 space-y-2.5">
            {card.shareable_fields.map((f) => (
              <li key={f} className="flex flex-col gap-1.5 sm:flex-row sm:items-center">
                <label className="flex w-52 cursor-pointer items-center gap-2 text-sm">
                  <input type="checkbox" checked={!!card.fields[f]} onChange={() => toggle(f)}
                    data-testid={`card-toggle-${f}`} className="h-4 w-4 rounded border-input" />
                  {FIELD_LABELS[f]}
                </label>
                <Input value={card.values[f] || ""} onChange={(e) => setValue(f, e.target.value)}
                  data-testid={`card-value-${f}`} aria-label={`${FIELD_LABELS[f]} value`}
                  disabled={!card.fields[f]} className="h-9 flex-1"
                  placeholder={card.profile_derived.includes(f)
                    ? (card.profile[f] || "From your profile") : ""} />
              </li>
            ))}
          </ul>
          <div className="mt-4 flex items-center justify-between">
            <span className="text-xs text-muted-foreground" data-testid="card-selected-count">
              {selectedCount} field(s) selected
            </span>
            <Button size="sm" onClick={save} disabled={saving} data-testid="card-save">
              {saving ? "Saving…" : "Save card"}
            </Button>
          </div>
        </section>

        <aside className="space-y-4">
          <section className="rounded-xl border border-border bg-card p-4" data-testid="card-share">
            <h2 className="text-base font-[640]">Share</h2>
            <p className="mt-1 text-xs text-muted-foreground">
              Share links work for signed-in Startup Otter members and expire after 30 days.
            </p>
            <div className="mt-3 flex flex-wrap gap-2">
              <Button size="sm" onClick={createShare} data-testid="card-create-share">
                <Share2 className="mr-1.5 h-4 w-4" /> New link
              </Button>
              <a href={`${process.env.REACT_APP_BACKEND_URL}/api/crm/card/vcard`}
                data-testid="card-vcard">
                <Button size="sm" variant="outline">
                  <Download className="mr-1.5 h-4 w-4" /> vCard
                </Button>
              </a>
            </div>

            {newShare && (
              <div className="mt-4 rounded-lg border border-primary/40 bg-primary/5 p-3"
                data-testid="card-new-share">
                <div className="flex justify-center rounded bg-white p-3">
                  <QRCodeSVG value={shareUrl} size={132} />
                </div>
                <p className="mt-2 break-all text-[11px] text-muted-foreground">{shareUrl}</p>
                <Button size="sm" variant="outline" className="mt-2 w-full"
                  data-testid="card-copy-link"
                  onClick={() => {
                    navigator.clipboard?.writeText(shareUrl);
                    toast.success("Link copied");
                  }}>
                  <Copy className="mr-1.5 h-4 w-4" /> Copy link
                </Button>
              </div>
            )}

            <ul className="mt-4 space-y-1.5" data-testid="card-share-list">
              {shares.length === 0 && (
                <li className="text-sm text-muted-foreground">No share links yet.</li>
              )}
              {shares.map((s) => (
                <li key={s.id} data-testid={`card-share-${s.id}`}
                  className="flex items-center justify-between gap-2 rounded-lg border border-border px-3 py-2 text-xs">
                  <span className="min-w-0 flex-1 truncate">
                    {s.fields?.length} field(s) · {s.state} · {s.uses} view(s)
                  </span>
                  {s.state === "active" && (
                    <button type="button" onClick={() => revoke(s.id)} aria-label="Revoke link"
                      data-testid={`card-revoke-${s.id}`}
                      className="rounded p-1 text-muted-foreground hover:text-destructive">
                      <Trash2 className="h-4 w-4" />
                    </button>
                  )}
                </li>
              ))}
            </ul>
          </section>
        </aside>
      </div>
    </div>
  );
}
